import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import {
    ClipboardList, CheckCircle2, Clock, Truck,
    User, ArrowRight, RefreshCw, AlertCircle, Package,
    Eye, X, Printer, Search,
    LogOut, History, Ruler, Pause, Play, Wrench, ShieldCheck
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Shell = styled.div`
  min-height: 100vh;
  display: grid;
  grid-template-columns: 240px 1fr;
  background: #f1f5f9;
`;

const Sidebar = styled.aside`
  background: #0f172a;
  color: #e2e8f0;
  padding: 1.25rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;
  .brand {
    display: flex; align-items: center; gap: 0.6rem;
    padding: 0.25rem 0.4rem 1rem;
    border-bottom: 1px solid #1e293b; margin-bottom: 0.25rem;
  }
  .brand .icon {
    width: 36px; height: 36px; border-radius: 10px;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    display: flex; align-items: center; justify-content: center; color: white; flex-shrink: 0;
  }
  .brand h1 { margin: 0; font-size: 1rem; font-weight: 800; color: white; }
  .brand p  { margin: 0; font-size: 0.7rem; color: #94a3b8; }
  .nav { display: flex; flex-direction: column; gap: 0.2rem; }
  .me {
    margin-top: auto; padding: 0.6rem 0.5rem; border-top: 1px solid #1e293b;
    display: flex; align-items: center; gap: 0.55rem; font-size: 0.78rem; color: #cbd5e1;
    .av { width: 30px; height: 30px; border-radius: 50%; background: #10b981; color: white;
          display: flex; align-items: center; justify-content: center; font-weight: 700; flex-shrink: 0; }
    .role { color: #64748b; font-size: 0.7rem; }
    .logout { margin-left: auto; background: transparent; border: none; color: #f87171; cursor: pointer; padding: 4px; border-radius: 6px; }
    .logout:hover { background: rgba(239, 68, 68, 0.1); }
  }
`;

const NavItem = styled.button`
  display: flex; align-items: center; gap: 0.55rem; padding: 0.55rem 0.65rem;
  border-radius: 9px;
  border: 1px solid ${(p) => (p.active ? 'rgba(16, 185, 129, 0.2)' : 'transparent')};
  background: ${(p) => (p.active ? 'rgba(16, 185, 129, 0.1)' : 'transparent')};
  color: ${(p) => (p.active ? '#10b981' : '#94a3b8')};
  font-size: 0.84rem; font-weight: ${(p) => (p.active ? 600 : 500)};
  cursor: pointer; text-align: left; width: 100%;
  transition: background 0.15s, border-color 0.15s;
  &:hover {
    background: ${(p) => (p.active ? 'rgba(16, 185, 129, 0.15)' : '#1e293b')};
    color: ${(p) => (p.active ? '#10b981' : 'white')};
  }
  .badge {
    margin-left: auto;
    background: ${(p) => (p.active ? 'rgba(16, 185, 129, 0.2)' : '#1e293b')};
    color: ${(p) => (p.active ? '#10b981' : '#94a3b8')};
    padding: 1px 7px; border-radius: 999px; font-size: 0.7rem; font-weight: 700; min-width: 22px; text-align: center;
  }
`;

const Main = styled.main`
  padding: 1.5rem 2rem 3rem; max-width: 1280px; width: 100%; margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 1.25rem; gap: 1rem; flex-wrap: wrap;
  animation: ${fadeIn} 0.25s ease;
  h2 { margin: 0; font-size: 1.4rem; font-weight: 800; color: #0f172a; }
  p  { margin: 4px 0 0; font-size: 0.85rem; color: #64748b; }
  .right { display: flex; gap: 0.5rem; align-items: center; }
`;

const SearchBar = styled.div`
  position: relative; background: white; border: 1px solid #e2e8f0; border-radius: 10px;
  display: flex; align-items: center; gap: 0.4rem; padding: 0.45rem 0.7rem; min-width: 240px;
  transition: border-color 0.15s;
  &:focus-within { border-color: #10b981; box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.08); }
  input { border: none; outline: none; background: transparent; width: 100%; font-size: 0.85rem; color: #0f172a; }
  input::placeholder { color: #94a3b8; }
`;

const GhostButton = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem;
  padding: 0.5rem 0.9rem; border-radius: 9px; border: 1px solid #e2e8f0; background: white;
  color: #334155; font-size: 0.84rem; font-weight: 600; cursor: pointer; transition: background 0.15s;
  &:hover:not(:disabled) { background: #f1f5f9; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const PrimaryButton = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem;
  padding: 0.5rem 0.9rem; border-radius: 9px; border: none;
  background: ${(p) => p.bg || 'linear-gradient(135deg, #10b981 0%, #059669 100%)'};
  color: white; font-size: 0.84rem; font-weight: 700; cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const Card = styled.section`
  background: white; border: 1px solid #e2e8f0; border-radius: 16px; padding: 1.1rem 1.2rem;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06); animation: ${fadeIn} 0.25s ease; margin-bottom: 1rem;
`;

const SectionLabel = styled.h3`
  margin: 0 0 0.75rem; font-size: 0.95rem; font-weight: 700; color: #0f172a;
  display: flex; align-items: center; gap: 0.5rem;
`;

const Grid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 1rem;
`;

const OrderCard = styled.div`
  background: white; border-radius: 14px; border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  display: flex; flex-direction: column; gap: 0.65rem; padding: 1rem;
  position: relative; overflow: hidden;
  transition: box-shadow 0.15s, border-color 0.15s;
  animation: ${fadeIn} 0.2s ease;
  &:hover { box-shadow: 0 4px 14px rgba(15, 23, 42, 0.1); border-color: #cbd5e1; }
  &::before {
    content: ''; position: absolute; top: 0; left: 0; width: 3px; height: 100%;
    border-radius: 3px 0 0 3px;
    background: ${(p) => {
      const s = p.status?.toLowerCase();
      if (s === 'manufacturing') return '#3b82f6';
      if (s === 'quality_check') return '#f59e0b';
      if (s === 'sent_to_store' || s === 'ready_for_pickup' || s === 'delivered') return '#10b981';
      if (s === 'assigned') return '#10b981';
      return '#e2e8f0';
    }};
  }
`;

const StatusPill = styled.span`
  display: inline-flex; align-items: center; gap: 0.25rem;
  padding: 0.125rem 0.55rem; border-radius: 999px; font-size: 0.72rem; font-weight: 700;
  background: ${(p) => p.bg || '#e0e7ef'}; color: ${(p) => p.color || '#475569'};
`;

const DetailRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  font-size: 0.84rem; padding-bottom: 0.35rem; border-bottom: 1px solid #f8fafc;
  &:last-child { border-bottom: none; padding-bottom: 0; }
`;

const Label = styled.span`color: #94a3b8; font-weight: 500;`;
const Value = styled.span`color: #334155; font-weight: 600;`;
const Empty = styled.div`padding: 2.5rem 1rem; text-align: center; color: #94a3b8; font-size: 0.9rem;`;

const ModalOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(15, 23, 42, 0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; backdrop-filter: blur(2px); padding: 1rem;
`;

const ModalContent = styled.div`
  background: white; border-radius: 20px; width: 100%; max-width: 860px; max-height: 88vh;
  overflow-y: auto; box-shadow: 0 25px 50px rgba(15, 23, 42, 0.25); animation: ${fadeIn} 0.2s ease;
`;

const ModalHeader = styled.div`
  padding: 1.25rem 1.4rem; border-bottom: 1px solid #f1f5f9;
  display: flex; justify-content: space-between; align-items: center;
  position: sticky; top: 0; background: white; z-index: 10;
`;

const ModalTitle = styled.h2`
  font-size: 1.1rem; font-weight: 800; color: #0f172a; margin: 0;
  display: flex; align-items: center; gap: 0.6rem;
`;

const ModalBody = styled.div`padding: 1.4rem;`;

const ModalSection = styled.div`
  margin-bottom: 1.5rem; padding-bottom: 1.25rem; border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; margin-bottom: 0; }
`;

const ModalSectionTitle = styled.h3`
  font-size: 0.75rem; font-weight: 700; color: #64748b; text-transform: uppercase;
  letter-spacing: 0.5px; margin: 0 0 0.85rem; display: flex; align-items: center; gap: 0.4rem;
`;

const DetailGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 1rem;`;
const DetailItem = styled.div`display: flex; flex-direction: column; gap: 4px;`;
const DetailLabel = styled.span`font-size: 0.72rem; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px;`;
const DetailValue = styled.span`font-size: 0.9rem; color: #0f172a; font-weight: ${(p) => (p.bold ? 700 : 400)};`;

const PrescriptionGrid = styled.div`
  display: grid; grid-template-columns: 80px repeat(5, 1fr);
  border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; margin-top: 0.75rem;
`;

const GridHeader = styled.div`
  background: #f8fafc; padding: 0.55rem 0.4rem; font-weight: 700; text-align: center;
  font-size: 0.72rem; color: #64748b; border-bottom: 1px solid #e2e8f0; border-right: 1px solid #e2e8f0;
  &:last-child { border-right: none; }
`;

const GridCell = styled.div`
  background: white; padding: 0.65rem 0.4rem; text-align: center; font-size: 0.85rem;
  color: #334155; font-weight: 500; font-family: ui-monospace, monospace;
  border-bottom: 1px solid #f1f5f9; border-right: 1px solid #f1f5f9;
  &:last-child { border-right: none; }
`;

const PauseModalShell = styled.div`
  background: white; border-radius: 16px; padding: 1.25rem 1.4rem 1.1rem;
  width: 100%; max-width: 460px; box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
  animation: ${fadeIn} 0.2s ease;
  .head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.85rem; }
  h3 { margin: 0; font-size: 1rem; font-weight: 800; color: #0f172a; }
  label { font-size: 0.82rem; color: #475569; font-weight: 600; display: block; margin-bottom: 0.35rem; }
  select, textarea {
    width: 100%; border: 1px solid #cbd5e1; border-radius: 10px; padding: 0.55rem 0.7rem;
    font-size: 0.88rem; font-family: inherit; background: white; color: #0f172a; box-sizing: border-box;
  }
  textarea { resize: vertical; min-height: 70px; }
  .row { display: flex; flex-direction: column; gap: 0.4rem; margin-bottom: 0.85rem; }
  .actions { display: flex; gap: 0.5rem; justify-content: flex-end; }
`;

const HOLD_REASONS = [
    { code: 'lens_broken',         label: 'Lens broken' },
    { code: 'frame_broken',        label: 'Frame broken' },
    { code: 'machine_malfunction', label: 'Machine malfunction' },
    { code: 'missing_part',        label: 'Missing part' },
    { code: 'waiting_supplier',    label: 'Waiting on supplier' },
    { code: 'rework_needed',       label: 'Rework needed' },
    { code: 'other',               label: 'Other' },
];

const getStatusPill = (status) => {
    switch (status) {
        case 'confirmed': case 'in_production': return { bg: '#e0e7ff', color: '#3730a3' };
        case 'manufacturing': return { bg: '#dbeafe', color: '#1e40af' };
        case 'quality_check': return { bg: '#fef3c7', color: '#92400e' };
        case 'sent_to_store': case 'ready_for_pickup': case 'delivered': return { bg: '#dcfce7', color: '#166534' };
        default: return { bg: '#f1f5f9', color: '#64748b' };
    }
};

const TechnicianDashboard = () => {
    const { t } = useTranslation();
    const [orders, setOrders] = useState([]);
    const [availableOrders, setAvailableOrders] = useState([]);
    const [assignedByManager, setAssignedByManager] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [activeTab, setActiveTab] = useState('assigned');
    const [searchQuery, setSearchQuery] = useState('');
    const [historyOrders, setHistoryOrders] = useState([]);
    const [pauseModal, setPauseModal] = useState(null);
    const [pauseReason, setPauseReason] = useState('lens_broken');
    const [pauseNotes, setPauseNotes] = useState('');
    const [pauseBusy, setPauseBusy] = useState(false);
    const [loadError, setLoadError] = useState('');
    const navigate = useNavigate();

    const fetchOrders = async () => {
        try {
            setLoading(true);
            setLoadError('');
            const token = localStorage.getItem('token');
            const [assignedRes, availableRes, byMgrRes] = await Promise.all([
                axios.get('/api/orders/technician/assigned', { headers: { Authorization: `Bearer ${token}` } }),
                axios.get('/api/orders/technician/available', { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { orders: [] } })),
                axios.get('/api/orders/technician/assigned-by-manager', { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
            ]);
            setOrders(assignedRes.data.orders || []);
            setAvailableOrders(availableRes.data.orders || []);
            setAssignedByManager(byMgrRes?.data?.orders || assignedRes.data.orders || []);
        } catch (err) {
            console.error('Error fetching orders:', err);
            setLoadError(err.response?.data?.message || err.message || 'Could not load technician orders');
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async () => {
        try {
            setLoading(true);
            setLoadError('');
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/orders/technician/history', { headers: { Authorization: `Bearer ${token}` } });
            setHistoryOrders(res.data.orders || []);
        } catch (err) {
            console.error('Error fetching history:', err);
            setLoadError(err.response?.data?.message || err.message || 'Could not load technician history');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'history') fetchHistory();
        else fetchOrders();
    }, [activeTab]);

    const handleLogout = () => { localStorage.clear(); navigate('/login'); };

    const handleWorkflowUpdate = async (orderId, newStatus, note) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`/api/orders/${orderId}/status`, { status: newStatus, notes: note }, { headers: { Authorization: `Bearer ${token}` } });
            fetchOrders();
            setSelectedOrder(null);
        } catch (err) { alert('Failed to update status'); }
    };

    const handleSelfAssign = async (orderId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`/api/orders/${orderId}/status`, { status: 'manufacturing', notes: 'Technician picked up order' }, { headers: { Authorization: `Bearer ${token}` } });
            fetchOrders();
            setActiveTab('my_queue');
        } catch (err) { alert('Failed to pick up order'); }
    };

    const submitPause = async () => {
        if (!pauseModal?.order) return;
        setPauseBusy(true);
        try {
            const token = localStorage.getItem('token');
            const reasonObj = HOLD_REASONS.find((r) => r.code === pauseReason);
            await axios.post(`/api/orders/${pauseModal.order._id}/hold`,
                { reasonCode: pauseReason, reason: reasonObj ? reasonObj.label : 'Other', notes: pauseNotes || null },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPauseModal(null); setPauseNotes(''); setPauseReason('lens_broken');
            fetchOrders();
        } catch (err) { alert(err.response?.data?.message || 'Failed to pause'); }
        finally { setPauseBusy(false); }
    };

    const resumeOrder = async (order) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/api/orders/${order._id}/release`, {}, { headers: { Authorization: `Bearer ${token}` } });
            fetchOrders();
        } catch (err) { alert(err.response?.data?.message || 'Failed to resume'); }
    };

    const filterOrders = (list) => {
        if (!searchQuery) return list;
        const q = searchQuery.toLowerCase();
        return list.filter(o =>
            o.orderNumber?.toLowerCase().includes(q) ||
            o.visitor?.firstName?.toLowerCase().includes(q) ||
            o.visitor?.lastName?.toLowerCase().includes(q)
        );
    };

    const handlePrintJobSheet = () => {
        if (!selectedOrder) return;
        const printWindow = window.open('', '_blank');
        const p = selectedOrder.prescription || {};
        const m = selectedOrder.measurements || {};
        const getTechName = () => {
            try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'N/A'; } catch { return 'N/A'; }
        };
        const genTable = (title, r, l) => `<h4>${title}</h4><div class="g">
            <div class="c h">Eye</div><div class="c h">SPH</div><div class="c h">CYL</div><div class="c h">AXIS</div><div class="c h">ADD</div><div class="c h">PD</div>
            <div class="c rh">OD</div><div class="c">${r?.sphere||'-'}</div><div class="c">${r?.cylinder||'-'}</div><div class="c">${r?.axis||'-'}</div><div class="c">${r?.add||'-'}</div><div class="c">${r?.pd||'-'}</div>
            <div class="c rh">OS</div><div class="c">${l?.sphere||'-'}</div><div class="c">${l?.cylinder||'-'}</div><div class="c">${l?.axis||'-'}</div><div class="c">${l?.add||'-'}</div><div class="c">${l?.pd||'-'}</div>
        </div>`;
        let tables = '';
        if (p.prescriptionType === 'single_vision') {
            if (p.singleVisionCorrectionType === 'far') tables = genTable('Distance', p.rightEyeFar, p.leftEyeFar);
            else if (p.singleVisionCorrectionType === 'closeup') tables = genTable('Reading', p.rightEyeCloseUp, p.leftEyeCloseUp);
            else tables = genTable('Single Vision', p.rightEye, p.leftEye);
        } else if (p.prescriptionType === 'progressive') {
            if (!p.progressiveFarNotAvailable) tables += genTable('Distance', p.progressiveRightEyeFar, p.progressiveLeftEyeFar);
            if (!p.progressiveCloseUpNotAvailable) tables += genTable('Reading', p.progressiveRightEyeCloseUp, p.progressiveLeftEyeCloseUp);
        } else { tables = genTable('Lens Correction', p.rightEye, p.leftEye); }
        const css = '<style>body{font-family:Segoe UI,sans-serif;padding:40px;color:#333;max-width:800px;margin:0 auto}.hdr{text-align:center;border-bottom:3px solid #0f172a;padding-bottom:20px;margin-bottom:30px}.logo{font-size:24px;font-weight:800;color:#0f172a;text-transform:uppercase}.meta{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:30px;font-size:14px;background:#f8fafc;padding:15px;border-radius:8px}h3{border-bottom:1px solid #ccc;padding-bottom:8px;margin-top:30px;color:#0f172a}.g{display:grid;grid-template-columns:100px repeat(5,1fr);border:1px solid #0f172a;margin:10px 0 20px}.c{padding:10px;text-align:center;border-right:1px solid #ccc;border-bottom:1px solid #ccc}.h{background:#f1f5f9;font-weight:bold}.rh{font-weight:bold;background:#f8fafc}ul{list-style:none;padding:0}li{padding:8px 0;border-bottom:1px dashed #ccc}.ftr{margin-top:50px;text-align:center;font-size:12px;color:#888;border-top:1px solid #eee;padding-top:20px}</style>';
        const html = `<html><head><title>Job Sheet #${selectedOrder.orderNumber}</title>${css}</head><body>
            <div class="hdr"><div class="logo">Optiva Lab</div><h1>Job Sheet #${selectedOrder.orderNumber}</h1></div>
            <div class="meta">
                <div><b>Patient:</b> ${p.patientName || selectedOrder.visitor?.firstName || 'Guest'}</div>
                <div><b>DOB:</b> ${p.patientBirthday ? new Date(p.patientBirthday).toLocaleDateString() : 'N/A'}</div>
                <div><b>Gender:</b> ${selectedOrder.visitor?.gender || 'N/A'}</div>
                <div><b>Age:</b> ${p.patientAge || selectedOrder.visitor?.ageGroup || 'N/A'}</div>
                <div><b>Order Date:</b> ${new Date().toLocaleDateString()}</div>
                <div><b>Tech:</b> ${getTechName()}</div>
            </div>
            <h3>Mesures</h3>
            <div style="background:#f8fafc;padding:15px;border-radius:8px;border:1px solid #e2e8f0;margin-bottom:16px">
                <div style="display:flex;justify-content:space-between;font-size:13px;margin-bottom:6px"><span>Hauteur de montage:</span><b>${m.mountingHeightOD != null || m.mountingHeightOG != null ? `OD ${m.mountingHeightOD ?? '-'} · OG ${m.mountingHeightOG ?? '-'}` : (m.mountingHeight ?? '-')} mm</b></div>
                <div style="display:flex;justify-content:space-between;font-size:13px;margin-bottom:6px"><span>PD OD:</span><b>${m.pdOD ?? m.pd ?? p.pupillaryDistance ?? '-'} mm</b></div>
                <div style="display:flex;justify-content:space-between;font-size:13px"><span>PD OG:</span><b>${m.pdOG ?? '-'} mm</b></div>
                ${m.near ? `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #e2e8f0;font-size:13px"><b>Paire 2 · près</b> — Hauteur OD ${m.near.mountingHeightOD ?? '-'} · OG ${m.near.mountingHeightOG ?? '-'} mm · PD OD ${m.near.pdOD ?? '-'} · OG ${m.near.pdOG ?? '-'} mm</div>` : ''}
            </div>
            <h3>Lens (${(p.prescriptionType||'Unknown').replace('_',' ').toUpperCase()})</h3>${tables}
            <h3>Frame &amp; Lenses</h3><ul>${selectedOrder.items?.map(i=>`<li><b>${i.type}</b>: ${i.brand} ${i.model} - ${i.material} ${i.color||''}</li>`).join('')||''}</ul>
            ${selectedOrder.specialInstructions?`<h3>Instructions</h3><div style="background:#fff3cd;padding:15px;border:1px solid #ffeeba">${selectedOrder.specialInstructions}</div>`:''}
            <div class="ftr">Optiva - Generated ${new Date().toLocaleString()}</div>
            <script>window.onload=()=>setTimeout(()=>window.print(),500);</script>
        </body></html>`;
        printWindow.document.write(html);
        printWindow.document.close();
    };

    const visibleAssigned = filterOrders(orders);
    const manufacturingOrders = visibleAssigned.filter(o => o.status === 'manufacturing');
    const qcOrders = visibleAssigned.filter(o => o.status === 'quality_check');
    const readyOrders = visibleAssigned.filter(o => ['sent_to_store', 'ready_for_pickup', 'delivered'].includes(o.status));
    const bucketedOrderIds = new Set([
        ...manufacturingOrders,
        ...qcOrders,
        ...readyOrders,
    ].map((o) => o._id));
    const newAssigned = visibleAssigned.filter(o => !bucketedOrderIds.has(o._id));
    const userName = localStorage.getItem('user_name') || 'Technician';
    const userRole = localStorage.getItem('user_role') || 'technician';
    const TABS = [
        { id: 'assigned',  label: 'Assigned to me',  icon: Wrench,        count: assignedByManager.length },
        { id: 'available', label: 'Available orders', icon: Package,       count: availableOrders.length },
        { id: 'my_queue',  label: 'My queue',         icon: ClipboardList, count: orders.length },
        { id: 'history',   label: 'History',          icon: History,       count: null },
    ];

    return (
        <Shell>
            <Sidebar>
                <div className="brand">
                    <div className="icon"><Wrench size={18} /></div>
                    <div><h1>{t('technician.optiva')}</h1><p>{t('technician.technician')}</p></div>
                </div>
                <div className="nav">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        return (
                            <NavItem key={t.id} active={activeTab === t.id} onClick={() => setActiveTab(t.id)}>
                                <Icon size={14} />{t.label}
                                {t.count != null && <span className="badge">{t.count}</span>}
                            </NavItem>
                        );
                    })}
                </div>
                <div className="me">
                    <div className="av">{userName.charAt(0).toUpperCase()}</div>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</div>
                        <div className="role">{userRole.replace('_', ' ')}</div>
                    </div>
                    <button className="logout" onClick={handleLogout} title={t('technician.logout')}><LogOut size={16} /></button>
                </div>
            </Sidebar>

            <Main>
                <PageHeader>
                    <div>
                        <h2>{activeTab === 'assigned' ? t('technician.assignedToMe') : activeTab === 'my_queue' ? t('technician.myQueue') : activeTab === 'available' ? t('technician.availableOrders') : t('technician.history')}</h2>
                        <p>{activeTab === 'assigned' ? t('technician.workTheLabManagerHas') : activeTab === 'available' ? t('technician.pickAnOrderAndStart') : activeTab === 'my_queue' ? t('technician.allOrdersCurrentlyInYour') : t('technician.completedAndDeliveredOrders')}</p>
                    </div>
                    <div className="right">
                        <SearchBar>
                            <Search size={15} color="#94a3b8" />
                            <input placeholder={t('technician.searchOrders')} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                        </SearchBar>
                        <GhostButton onClick={fetchOrders} disabled={loading}>
                            <RefreshCw size={14} /> {loading ? t('technician.loading') : t('technician.refresh')}
                        </GhostButton>
                    </div>
                </PageHeader>

                {loadError && (
                    <Card style={{ borderColor: '#fecaca', background: '#fff7ed', color: '#991b1b', marginBottom: '1rem' }}>
                        {loadError}
                    </Card>
                )}

                {activeTab === 'assigned' && (
                    <Card>
                        <SectionLabel><Wrench size={16} /> {t('technician.assignedToYou')}{filterOrders(assignedByManager).length})</SectionLabel>
                        {filterOrders(assignedByManager).length === 0 ? (
                            <Empty>{t('technician.nothingAssignedToYouYet')}</Empty>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                                {filterOrders(assignedByManager).map((order) => {
                                    const latestStatus = order.workflow?.[order.workflow.length - 1]?.status || order.status;
                                    const isInQc = ['manufacturing_completed', 'quality_check', 'quality_check_passed'].includes(latestStatus);
                                    return (
                                    <div key={order._id} style={{
                                        border: `1px solid ${order.onHold ? '#fde68a' : '#bbf7d0'}`,
                                        borderRadius: 14,
                                        background: order.onHold ? 'linear-gradient(180deg,#fffbeb 0%,#fff 100%)' : 'linear-gradient(180deg,#f0fdf4 0%,#fff 100%)',
                                        padding: '0.85rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.55rem',
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                <strong style={{ color: '#0f172a', fontFamily: 'ui-monospace,monospace' }}>{order.orderNumber}</strong>
                                                {isInQc
                                                    ? <StatusPill bg="#fef3c7" color="#92400e"><ShieldCheck size={11} /> {t('technician.withLabManager')}</StatusPill>
                                                    : order.onHold
                                                    ? <StatusPill bg="#fef3c7" color="#92400e"><Pause size={11} /> {t('technician.onHold')}</StatusPill>
                                                    : <StatusPill bg="#dcfce7" color="#166534"><Wrench size={11} /> {t('technician.inProgress')}</StatusPill>
                                                }
                                                {order.assignedBy?.firstName && (
                                                    <StatusPill bg="#e2e8f0" color="#334155"><User size={11} /> by {order.assignedBy.firstName}</StatusPill>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                                                <GhostButton onClick={() => setSelectedOrder(order)}><Eye size={13} /> {t('technician.details')}</GhostButton>
                                                {isInQc ? (
                                                    <StatusPill bg="#fef3c7" color="#92400e"><ShieldCheck size={11} /> {t('technician.awaitingQc')}</StatusPill>
                                                ) : order.onHold ? (
                                                    <PrimaryButton onClick={() => resumeOrder(order)} bg="linear-gradient(135deg,#3b82f6 0%,#1d4ed8 100%)">
                                                        <Play size={13} /> {t('technician.resume')}
                                                    </PrimaryButton>
                                                ) : (
                                                    <>
                                                        <GhostButton onClick={() => { setPauseModal({ order }); setPauseReason('lens_broken'); setPauseNotes(''); }} style={{ borderColor: '#fde68a', color: '#92400e' }}>
                                                            <Pause size={13} /> {t('technician.pause')}
                                                        </GhostButton>
                                                        <PrimaryButton onClick={() => handleWorkflowUpdate(order._id, 'quality_check', 'Lens completed — handed to lab manager for QC')}>
                                                            <CheckCircle2 size={13} /> {t('technician.handToLabManager')}
                                                        </PrimaryButton>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        {order.onHold && order.holdReason && (
                                            <div style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#854d0e', borderRadius: 10, padding: '0.45rem 0.7rem', fontSize: '0.82rem' }}>
                                                <strong>{t('technician.onHold2')}</strong> {order.holdReason}
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', color: '#475569', fontSize: '0.85rem', alignItems: 'center' }}>
                                            <strong style={{ color: '#0f172a' }}>
                                                {order.visitor ? [order.visitor.firstName, order.visitor.lastName].filter(Boolean).join(' ') : t('technician.customer')}
                                            </strong>
                                            {order.store?.name && <span>{t('technician.from')} {order.store.name}</span>}
                                            <StatusPill {...getStatusPill(order.status)}>{(order.status||'').replace(/_/g,' ')}</StatusPill>
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </Card>
                )}

                {activeTab === 'available' && (
                    filterOrders(availableOrders).length === 0 ? <Card><Empty>{t('technician.noAvailableOrdersCheckBack')}</Empty></Card> : (
                        <Grid>
                            {filterOrders(availableOrders).map(order => (
                                <OrderCard key={order._id} status="available">
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                        <div style={{ fontFamily: 'ui-monospace,monospace', fontWeight: 700, color: '#0f172a' }}>{order.orderNumber}</div>
                                        <StatusPill bg="#dcfce7" color="#166534">{t('technician.available')}</StatusPill>
                                    </div>
                                    <DetailRow><Label>{t('technician.customer')}</Label><Value>{order.visitor?.firstName} {order.visitor?.lastName}</Value></DetailRow>
                                    <DetailRow><Label>{t('technician.lens')}</Label><Value>{order.items?.find(i => i.type === 'lens')?.brand || 'Standard'}</Value></DetailRow>
                                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: 'auto', paddingTop: '0.25rem' }}>
                                        <GhostButton style={{ flex: 1 }} onClick={() => setSelectedOrder(order)}><Eye size={13} /> {t('technician.details')}</GhostButton>
                                        <PrimaryButton style={{ flex: 1 }} onClick={() => handleSelfAssign(order._id)}>{t('technician.start')} <ArrowRight size={13} /></PrimaryButton>
                                    </div>
                                </OrderCard>
                            ))}
                        </Grid>
                    )
                )}

                {activeTab === 'my_queue' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        {newAssigned.length > 0 && <div>
                            <SectionLabel style={{ marginBottom: '0.75rem' }}><ClipboardList size={16} /> {t('technician.assignedToStart')}</SectionLabel>
                            <Grid>{newAssigned.map(order => (
                                <OrderCard key={order._id} status={order.status}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                        <div style={{ fontFamily: 'ui-monospace,monospace', fontWeight: 700, color: '#0f172a' }}>{order.orderNumber}</div>
                                        <StatusPill {...getStatusPill(order.status)}>{(order.status||'').replace(/_/g,' ')}</StatusPill>
                                    </div>
                                    <DetailRow><Label>{t('technician.customer')}</Label><Value>{order.visitor?.firstName} {order.visitor?.lastName}</Value></DetailRow>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <GhostButton style={{ flex: 1 }} onClick={() => setSelectedOrder(order)}><Eye size={13} /> {t('technician.view')}</GhostButton>
                                        <PrimaryButton style={{ flex: 1 }} onClick={() => handleWorkflowUpdate(order._id, 'manufacturing', 'Started manufacturing')}>{t('technician.start')} <ArrowRight size={13} /></PrimaryButton>
                                    </div>
                                </OrderCard>
                            ))}</Grid>
                        </div>}
                        {manufacturingOrders.length > 0 && <div>
                            <SectionLabel style={{ marginBottom: '0.75rem' }}><Clock size={16} /> {t('technician.inManufacturing')}</SectionLabel>
                            <Grid>{manufacturingOrders.map(order => (
                                <OrderCard key={order._id} status={order.status}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                        <div style={{ fontFamily: 'ui-monospace,monospace', fontWeight: 700, color: '#0f172a' }}>{order.orderNumber}</div>
                                        <StatusPill bg="#dbeafe" color="#1e40af">{t('technician.manufacturing')}</StatusPill>
                                    </div>
                                    <DetailRow><Label>{t('technician.items')}</Label><Value>{order.items?.length||0} items</Value></DetailRow>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <GhostButton style={{ flex: 1 }} onClick={() => setSelectedOrder(order)}><Eye size={13} /> {t('technician.view')}</GhostButton>
                                        <PrimaryButton style={{ flex: 1 }} bg="linear-gradient(135deg,#f59e0b 0%,#d97706 100%)" onClick={() => handleWorkflowUpdate(order._id, 'quality_check', 'Manufacturing complete')}>
                                            <ShieldCheck size={13} /> {t('technician.sendToQc')}
                                        </PrimaryButton>
                                    </div>
                                </OrderCard>
                            ))}</Grid>
                        </div>}
                        {qcOrders.length > 0 && <div>
                            <SectionLabel style={{ marginBottom: '0.75rem' }}><ShieldCheck size={16} /> {t('technician.qualityCheck')}</SectionLabel>
                            <Grid>{qcOrders.map(order => (
                                <OrderCard key={order._id} status={order.status}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                        <div style={{ fontFamily: 'ui-monospace,monospace', fontWeight: 700, color: '#0f172a' }}>{order.orderNumber}</div>
                                        <StatusPill bg="#fef3c7" color="#92400e">{t('technician.qcPending')}</StatusPill>
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <GhostButton style={{ flex: 1 }} onClick={() => setSelectedOrder(order)}><Eye size={13} /> {t('technician.inspect')}</GhostButton>
                                        <PrimaryButton style={{ flex: 1 }} onClick={() => handleWorkflowUpdate(order._id, 'ready_for_pickup', 'QC Passed')}>
                                            <CheckCircle2 size={13} /> {t('technician.passQc')}
                                        </PrimaryButton>
                                    </div>
                                </OrderCard>
                            ))}</Grid>
                        </div>}
                        {readyOrders.length > 0 && <div>
                            <SectionLabel style={{ marginBottom: '0.75rem' }}><Truck size={16} /> {t('technician.readyDelivered')}</SectionLabel>
                            <Grid>{readyOrders.slice(0, 5).map(order => (
                                <OrderCard key={order._id} status={order.status} style={{ opacity: 0.8 }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                        <div style={{ fontFamily: 'ui-monospace,monospace', fontWeight: 700, color: '#0f172a' }}>{order.orderNumber}</div>
                                        <StatusPill {...getStatusPill(order.status)}>{(order.status||'').replace(/_/g,' ')}</StatusPill>
                                    </div>
                                    <GhostButton style={{ width: '100%' }} onClick={() => setSelectedOrder(order)}><Eye size={13} /> {t('technician.history')}</GhostButton>
                                </OrderCard>
                            ))}</Grid>
                        </div>}
                        {visibleAssigned.length === 0 && <Card><Empty>{t('technician.yourQueueIsEmptyGood')}</Empty></Card>}
                    </div>
                )}

                {activeTab === 'history' && (
                    filterOrders(historyOrders).length === 0 ? <Card><Empty>{t('technician.noCompletedOrdersFound')}</Empty></Card> : (
                        <Grid>
                            {filterOrders(historyOrders).map(order => (
                                <OrderCard key={order._id} status={order.status} style={{ opacity: 0.85 }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                        <div style={{ fontFamily: 'ui-monospace,monospace', fontWeight: 700, color: '#0f172a' }}>{order.orderNumber}</div>
                                        <StatusPill {...getStatusPill(order.status)}>{(order.status||'').replace(/_/g,' ')}</StatusPill>
                                    </div>
                                    <DetailRow><Label>{t('technician.customer')}</Label><Value>{order.visitor?.firstName} {order.visitor?.lastName}</Value></DetailRow>
                                    <DetailRow><Label>{t('technician.date')}</Label><Value>{new Date(order.updatedAt).toLocaleDateString()}</Value></DetailRow>
                                    <GhostButton style={{ width: '100%' }} onClick={() => setSelectedOrder(order)}><Eye size={13} /> {t('technician.viewDetails')}</GhostButton>
                                </OrderCard>
                            ))}
                        </Grid>
                    )
                )}
            </Main>

            {selectedOrder && (
                <ModalOverlay onClick={() => setSelectedOrder(null)}>
                    <ModalContent onClick={e => e.stopPropagation()}>
                        <ModalHeader>
                            <ModalTitle>
                                <div style={{ width: 36, height: 36, background: '#f1f5f9', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Wrench size={18} color="#475569" />
                                </div>
                                {t('technician.order')} {selectedOrder.orderNumber}
                            </ModalTitle>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <GhostButton onClick={handlePrintJobSheet}><Printer size={14} /> {t('technician.printJobSheet')}</GhostButton>
                                <GhostButton onClick={() => setSelectedOrder(null)}><X size={16} /></GhostButton>
                            </div>
                        </ModalHeader>
                        <ModalBody>
                            <ModalSection>
                                <ModalSectionTitle><User size={13} /> {t('technician.customerInfo')}</ModalSectionTitle>
                                <DetailGrid>
                                    <DetailItem><DetailLabel>{t('technician.fullName')}</DetailLabel><DetailValue bold>{selectedOrder.visitor?.firstName} {selectedOrder.visitor?.lastName}</DetailValue></DetailItem>
                                    <DetailItem><DetailLabel>{t('technician.ageGroup')}</DetailLabel><DetailValue>{selectedOrder.visitor?.ageGroup || 'N/A'}</DetailValue></DetailItem>
                                    <DetailItem><DetailLabel>{t('technician.technician')}</DetailLabel><DetailValue>{selectedOrder.technician?.firstName || 'Assigned to you'}</DetailValue></DetailItem>
                                </DetailGrid>
                            </ModalSection>
                            {selectedOrder.measurements && (
                                <ModalSection>
                                    <ModalSectionTitle><Ruler size={13} /> {t('technician.measurements')}</ModalSectionTitle>
                                    <DetailGrid>
                                        <DetailItem><DetailLabel>{t('technician.hauteurDeMontage')}</DetailLabel><DetailValue>{selectedOrder.measurements.mountingHeightOD != null || selectedOrder.measurements.mountingHeightOG != null
                                          ? `OD ${selectedOrder.measurements.mountingHeightOD ?? '-'} · OG ${selectedOrder.measurements.mountingHeightOG ?? '-'}`
                                          : (selectedOrder.measurements.mountingHeight ?? '-')} mm</DetailValue></DetailItem>
                                        <DetailItem><DetailLabel>{t('technician.pdOd')}</DetailLabel><DetailValue>{selectedOrder.measurements.pdOD ?? selectedOrder.measurements.pd ?? '-'} mm</DetailValue></DetailItem>
                                        <DetailItem><DetailLabel>{t('technician.pdOg')}</DetailLabel><DetailValue>{selectedOrder.measurements.pdOG ?? '-'} mm</DetailValue></DetailItem>
                                        {/* Two-pair sale: pair 2 (close up) measures. */}
                                        {selectedOrder.measurements.near && (
                                          <DetailItem>
                                            <DetailLabel>{t('pairs.pairNear')}</DetailLabel>
                                            <DetailValue>
                                              {`${t('technician.hauteurDeMontage')} OD ${selectedOrder.measurements.near.mountingHeightOD ?? '-'} · OG ${selectedOrder.measurements.near.mountingHeightOG ?? '-'} mm · PD OD ${selectedOrder.measurements.near.pdOD ?? '-'} · OG ${selectedOrder.measurements.near.pdOG ?? '-'} mm`}
                                            </DetailValue>
                                          </DetailItem>
                                        )}
                                    </DetailGrid>
                                </ModalSection>
                            )}
                            {selectedOrder.prescription && (
                                <ModalSection>
                                    <ModalSectionTitle><Eye size={13} /> {t('technician.prescription')} {selectedOrder.prescription.prescriptionType}</ModalSectionTitle>
                                    <PrescriptionGrid>
                                        <GridHeader>{t('technician.eye')}</GridHeader><GridHeader>{t('technician.sph')}</GridHeader><GridHeader>{t('technician.cyl')}</GridHeader><GridHeader>{t('technician.axis')}</GridHeader><GridHeader>{t('technician.add')}</GridHeader><GridHeader>{t('technician.pd')}</GridHeader>
                                        <GridCell style={{ fontWeight: 700 }}>{t('technician.od')}</GridCell>
                                        <GridCell>{selectedOrder.prescription.rightEye?.sphere ?? '—'}</GridCell>
                                        <GridCell>{selectedOrder.prescription.rightEye?.cylinder ?? '—'}</GridCell>
                                        <GridCell>{selectedOrder.prescription.rightEye?.axis ?? '—'}</GridCell>
                                        <GridCell>{selectedOrder.prescription.rightEye?.add ?? '—'}</GridCell>
                                        <GridCell>{selectedOrder.prescription.rightEye?.pd ?? '—'}</GridCell>
                                        <GridCell style={{ fontWeight: 700 }}>{t('technician.os')}</GridCell>
                                        <GridCell>{selectedOrder.prescription.leftEye?.sphere ?? '—'}</GridCell>
                                        <GridCell>{selectedOrder.prescription.leftEye?.cylinder ?? '—'}</GridCell>
                                        <GridCell>{selectedOrder.prescription.leftEye?.axis ?? '—'}</GridCell>
                                        <GridCell>{selectedOrder.prescription.leftEye?.add ?? '—'}</GridCell>
                                        <GridCell>{selectedOrder.prescription.leftEye?.pd ?? '—'}</GridCell>
                                    </PrescriptionGrid>
                                </ModalSection>
                            )}
                            <ModalSection>
                                <ModalSectionTitle><Package size={13} /> {t('technician.materials')}</ModalSectionTitle>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    {selectedOrder.items?.map((item, i) => (
                                        <div key={i} style={{ padding: '0.75rem 1rem', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9rem' }}>{item.brand} {item.model}</div>
                                                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{item.type} · {item.material} · {item.color}</div>
                                            </div>
                                            <span style={{ fontWeight: 600, color: '#334155' }}>×{item.quantity}</span>
                                        </div>
                                    ))}
                                </div>
                            </ModalSection>
                            {selectedOrder.specialInstructions && (
                                <ModalSection>
                                    <ModalSectionTitle><AlertCircle size={13} /> {t('technician.specialInstructions')}</ModalSectionTitle>
                                    <div style={{ padding: '0.75rem 1rem', background: '#fffbeb', color: '#92400e', borderRadius: 10, border: '1px solid #fcd34d', fontSize: '0.9rem' }}>
                                        {selectedOrder.specialInstructions}
                                    </div>
                                </ModalSection>
                            )}
                        </ModalBody>
                    </ModalContent>
                </ModalOverlay>
            )}

            {pauseModal && (
                <ModalOverlay onClick={() => setPauseModal(null)}>
                    <PauseModalShell onClick={(e) => e.stopPropagation()}>
                        <div className="head">
                            <h3><Pause size={15} style={{ verticalAlign: '-2px', marginRight: 6 }} /> {t('technician.pause2')} {pauseModal.order.orderNumber}</h3>
                            <button onClick={() => setPauseModal(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={18} /></button>
                        </div>
                        <div className="row">
                            <label>{t('technician.reason')}</label>
                            <select value={pauseReason} onChange={(e) => setPauseReason(e.target.value)}>
                                {HOLD_REASONS.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                            </select>
                        </div>
                        <div className="row">
                            <label>{t('technician.notesOptional')}</label>
                            <textarea value={pauseNotes} onChange={(e) => setPauseNotes(e.target.value)} placeholder={t('technician.whatHappenedWhatNeedsTo')} />
                        </div>
                        <div className="actions">
                            <GhostButton onClick={() => setPauseModal(null)}>{t('technician.cancel')}</GhostButton>
                            <PrimaryButton onClick={submitPause} disabled={pauseBusy} bg="linear-gradient(135deg,#f59e0b 0%,#d97706 100%)">
                                <Pause size={13} /> {pauseBusy ? t('technician.saving') : t('technician.pauseWork')}
                            </PrimaryButton>
                        </div>
                    </PauseModalShell>
                </ModalOverlay>
            )}
        </Shell>
    );
};

export default TechnicianDashboard;